import { HttpRequest } from '@angular/common/http';
import {
  computed,
  inject,
  Injectable,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  ExchangeToken,
  PasswordExpirationStatus,
  UserPasswordExpiration,
} from '@core/models/token.model';
import { EnumOption } from '@shared/models/options.model';
import { ToastService } from '@shared/services/toast/toast.service';

export interface BackendError {
  target: Record<string, unknown> | null;
  code: string;
  message: string;
  args: Record<string, unknown>[];
  errors: Record<string, unknown> | null;
}

export interface BackendResponse<T> {
  data?: T;
  message: string;
  status: number;
  timestamp?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ResponseService {
  expiresSoon: boolean | null = null;

  private readonly _eventStatusSignal: WritableSignal<number> = signal(500);
  private readonly _isToastExpiredSoonShownSignal: WritableSignal<boolean> =
    signal(false);
  private readonly _httpRequestSignal: WritableSignal<HttpRequest<unknown>> =
    signal({} as HttpRequest<unknown>);
  private readonly _responseBodySignal: WritableSignal<BackendResponse<
    Record<string, unknown>
  > | null> = signal(null);
  private readonly _isToastHeadersSignal = signal<string | null>(null);

  readonly isToastExpiredSoonShownSignal =
    this._isToastExpiredSoonShownSignal.asReadonly();
  readonly eventStatusSignal = this._eventStatusSignal.asReadonly();
  readonly isToastHeadersSignal = this._isToastHeadersSignal.asReadonly();
  readonly responseBodySignal = this._responseBodySignal.asReadonly();

  readonly isValidCRUDToast: Signal<boolean> = computed(() => {
    return (
      (!this._isResponseSuccessSignal() || !this._isResponseGetSignal()) &&
      !this._isPostResponseGetSignal()
    );
  });

  readonly isValidPasswordExpirationToast: Signal<boolean> = computed(() =>
    Boolean(
      (this.isToastHeadersSignal() &&
        this.responseBodySignal() &&
        !this.isPasswordExpirationResponse(this.responseBodySignal())) ||
        (this.isToastHeadersSignal() &&
          this.responseBodySignal() &&
          this.isPasswordExpirationResponse(this.responseBodySignal()) &&
          !!this.expiresSoon),
    ),
  );

  private readonly _isResponseSuccessSignal: Signal<boolean> = computed(
    () => this.eventStatusSignal() > 199 && this.eventStatusSignal() < 300,
  );

  private readonly _isResponseGetSignal: Signal<boolean> = computed(
    () => this._httpRequestSignal().method === 'GET',
  );

  private readonly _isPostResponseGetSignal: Signal<boolean> = computed(
    () =>
      this._httpRequestSignal().method === 'POST' &&
      this.eventStatusSignal() === 200,
  );

  private readonly _toastService = inject(ToastService);

  showPasswordExpirationToast(): void {
    if (this.isValidPasswordExpirationToast()) {
      this._toastService.showResponseToast(
        this.responseBodySignal()!.status,
        this.responseBodySignal()!.message,
      );
      this.setToastExpiredSoonShown(true);
    }
  }

  showGenericSuccessToast(status: number, message: string): void {
    if (this.isValidCRUDToast()) {
      this._toastService.showResponseToast(status, message);
    }
  }

  showErrorToast(status: number, message: string): void {
    this._toastService.showResponseToast(status, message);
  }

  //SETTERS
  setIsToastHeaders(showToastHeaders: string | null): void {
    this._isToastHeadersSignal.set(showToastHeaders);
  }

  setToastExpiredSoonShown(value: boolean): void {
    this._isToastExpiredSoonShownSignal.set(value);
  }

  setStatus(statusCode: number): void {
    this._eventStatusSignal.set(statusCode);
  }

  setHttpRequest(httpRequest: HttpRequest<unknown>): void {
    this._httpRequestSignal.set(httpRequest);
  }

  setResponseBodySignal(
    responseBody: BackendResponse<Record<string, unknown>> | null,
  ): void {
    this._responseBodySignal.set(responseBody);
  }

  // EXPIRATION PASSWORD CHECK METHODS
  isPasswordExpirationResponse(
    body: unknown,
  ): body is BackendResponse<ExchangeToken> {
    if (typeof body !== 'object' || body === null) return false;

    const record = body as Record<string, unknown>;
    return (
      this.isExchangeToken(record['data']) &&
      typeof record['message'] === 'string' &&
      typeof record['status'] === 'number'
    );
  }

  private isPasswordExpiringEnumOption(obj: unknown): obj is EnumOption {
    if (typeof obj !== 'object' || obj === null) return false;

    const record = obj as Record<string, unknown>;
    this.expiresSoon =
      record['value'] === PasswordExpirationStatus.EXPIRES_SOON;
    return (
      typeof record['label'] === 'string' && typeof record['value'] === 'string'
    );
  }

  private isUserPasswordExpiration(
    obj: unknown,
  ): obj is UserPasswordExpiration {
    if (typeof obj !== 'object' || obj === null) return false;

    const record = obj as Record<string, unknown>;
    return (
      typeof record['message'] === 'string' &&
      this.isPasswordExpiringEnumOption(record['status'])
    );
  }

  private isExchangeToken(obj: unknown): obj is ExchangeToken {
    if (typeof obj !== 'object' || obj === null) return false;

    const record = obj as Record<string, unknown>;
    return (
      typeof record['access_token'] === 'string' &&
      typeof record['expires_in'] === 'number' &&
      typeof record['token_type'] === 'string' &&
      this.isUserPasswordExpiration(record['user_password_expiration'])
    );
  }
}
